import { Application, Container, Graphics } from "pixi.js";
import type { Board, Kind, Pos } from "../core/types";
import type { ThemePack } from "../theme/types";
import { tileVisual } from "../theme/types";
import { TileSprite } from "./TileSprite";
import { tween, beam, burst, delay, easeOutBack, easeOutCubic, easeInOutQuad } from "./fx";

const PAD = 12; // 棋盘内边距
const GAP = 8; // 格子间隙

// 渲染层：把 core 的 Board 画成 Pixi 场景，并提供滑动/消除/提示等动画。
// 它对 core 一无所知的「怎么玩」，只负责「怎么显示」——因此可整体替换。
export class GameView {
  readonly app = new Application();

  private pack!: ThemePack;
  private board = new Container(); // 棋盘容器（局部坐标：左上角为原点）
  private slots = new Graphics(); // 底板 + 空格子
  private tilesLayer = new Container();
  private fxLayer = new Container();

  private tiles: (TileSprite | null)[][] = [];
  private current: Board = [];
  private cols = 0; // 列数（宽）
  private rows = 0; // 行数（高）

  private cell = 40;
  private step = 48;
  private boardX = 0;
  private boardY = 0;

  /** 拖拽/动画期间置真，阻止 resize 打断当前操作。 */
  suspendLayout = false;
  /** 视口尺寸变化时回调（控制器可用来同步 HUD）。 */
  onResize?: () => void;

  async init(mount: HTMLElement, pack: ThemePack): Promise<void> {
    this.pack = pack;
    await this.app.init({
      background: pack.background ?? 0x0f172a,
      resizeTo: window,
      antialias: true,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
    });
    mount.appendChild(this.app.canvas);

    this.tilesLayer.sortableChildren = true;
    this.board.addChild(this.slots, this.tilesLayer, this.fxLayer);
    this.app.stage.addChild(this.board);

    this.app.renderer.on("resize", () => {
      if (this.suspendLayout) return;
      this.relayout();
      this.onResize?.();
    });
  }

  get canvas(): HTMLCanvasElement {
    return this.app.canvas;
  }

  setTheme(pack: ThemePack): void {
    this.pack = pack;
    this.app.renderer.background.color = pack.background ?? 0x0f172a;
    if (this.current.length) this.renderBoard(this.current);
  }

  private cellCenter(x: number, y: number): { x: number; y: number } {
    return { x: PAD + x * this.step + this.cell / 2, y: PAD + y * this.step + this.cell / 2 };
  }

  // 矩形棋盘：两个方向分别按各自的可用空间与格子数计算，取较小的格子尺寸，
  // 保证整块棋盘在竖屏内既不溢出宽度也不溢出高度。
  private computeLayout(cols: number, rows: number): void {
    const sw = this.app.screen.width;
    const sh = this.app.screen.height;
    const chromeW = (cols - 1) * GAP + 2 * PAD;
    const chromeH = (rows - 1) * GAP + 2 * PAD;
    const budgetW = sw * 0.94 - chromeW;
    const budgetH = sh * 0.7 - chromeH;
    let cell = Math.floor(Math.min(budgetW / cols, budgetH / rows));
    // 上限防桌面过大；下限保底可点。下限设得足够小，确保满格 8×10 在窄屏手机上
    // 也由 budget 决定尺寸而不会因保底值被强行放大导致溢出。
    cell = Math.max(20, Math.min(84, cell));
    this.cell = cell;
    this.step = cell + GAP;
    const boardW = cols * cell + (cols - 1) * GAP + 2 * PAD;
    const boardH = rows * cell + (rows - 1) * GAP + 2 * PAD;
    this.boardX = Math.round((sw - boardW) / 2);
    this.boardY = Math.round((sh - boardH) / 2);
    this.board.position.set(this.boardX, this.boardY);
  }

  private drawSlots(cols: number, rows: number): void {
    const boardW = cols * this.cell + (cols - 1) * GAP + 2 * PAD;
    const boardH = rows * this.cell + (rows - 1) * GAP + 2 * PAD;
    this.slots.clear();
    this.slots.roundRect(0, 0, boardW, boardH, 28).fill({ color: this.pack.boardColor ?? 0x1e293b });
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const cx = PAD + x * this.step;
        const cy = PAD + y * this.step;
        this.slots
          .roundRect(cx, cy, this.cell, this.cell, this.cell * 0.22)
          .fill({ color: this.pack.emptyCellColor ?? 0x1b2740 });
      }
    }
  }

  /** 全量重建棋盘可视对象，与 board 数据对齐（动画结束后调用即可归位）。 */
  renderBoard(board: Board): void {
    this.current = board;
    this.rows = board.length;
    this.cols = board[0]?.length ?? 0;
    this.computeLayout(this.cols, this.rows);
    this.drawSlots(this.cols, this.rows);

    this.tilesLayer.removeChildren().forEach((c) => c.destroy({ children: true }));
    this.tiles = Array.from({ length: this.rows }, () => Array<TileSprite | null>(this.cols).fill(null));

    for (let y = 0; y < this.rows; y++) {
      for (let x = 0; x < this.cols; x++) {
        const kind = board[y][x];
        if (kind === null) continue;
        const t = new TileSprite(x, y);
        t.paint(kind, tileVisual(this.pack, kind), this.cell, this.pack.tileCardColor ?? 0xf8fafc);
        const c = this.cellCenter(x, y);
        t.position.set(c.x, c.y);
        this.tilesLayer.addChild(t);
        this.tiles[y][x] = t;
      }
    }
  }

  private relayout(): void {
    if (this.current.length) this.renderBoard(this.current);
  }

  tileAt(x: number, y: number): TileSprite | null {
    return this.tiles[y]?.[x] ?? null;
  }

  get cellSize(): number {
    return this.cell;
  }
  get stepSize(): number {
    return this.step;
  }

  /** 屏幕坐标 → 棋盘格子；点在间隙或界外时返回 null。 */
  screenToCell(clientX: number, clientY: number): Pos | null {
    const rect = this.app.canvas.getBoundingClientRect();
    const lx = clientX - rect.left - this.boardX - PAD;
    const ly = clientY - rect.top - this.boardY - PAD;
    if (lx < 0 || ly < 0) return null;
    const gx = Math.floor(lx / this.step);
    const gy = Math.floor(ly / this.step);
    if (gx < 0 || gy < 0 || gx >= this.cols || gy >= this.rows) return null;
    if (lx - gx * this.step > this.cell || ly - gy * this.step > this.cell) return null;
    return { x: gx, y: gy };
  }

  // —— 交互期实时位移（拖拽预览） ——
  dragOffset(group: Pos[], ox: number, oy: number): void {
    for (const p of group) {
      const t = this.tileAt(p.x, p.y);
      if (!t) continue;
      const c = this.cellCenter(p.x, p.y);
      t.position.set(c.x + ox, c.y + oy);
      t.zIndex = 99;
    }
  }

  resetGroup(group: Pos[], animated = true): Promise<void> {
    const items = group
      .map((p) => ({ t: this.tileAt(p.x, p.y), home: this.cellCenter(p.x, p.y) }))
      .filter((it): it is { t: TileSprite; home: { x: number; y: number } } => it.t !== null);
    const starts = items.map((it) => ({ x: it.t.x, y: it.t.y }));
    const finish = () => items.forEach((it) => (it.t.zIndex = 0));
    if (!animated) {
      items.forEach((it) => it.t.position.set(it.home.x, it.home.y));
      finish();
      return Promise.resolve();
    }
    return tween({
      duration: 170,
      ease: easeOutBack,
      onUpdate: (v) => {
        items.forEach((it, i) => {
          it.t.x = starts[i].x + (it.home.x - starts[i].x) * v;
          it.t.y = starts[i].y + (it.home.y - starts[i].y) * v;
        });
      },
      onComplete: finish,
    });
  }

  /** 播放整组滑动 steps 格的补间动画。 */
  animateSlide(group: Pos[], dx: number, dy: number, steps: number): Promise<void> {
    const movedX = dx * steps * this.step;
    const movedY = dy * steps * this.step;
    // 从「当前视觉位置」（拖拽已把方块偏移到手指处）补间到「绝对目标格」，
    // 而不是在当前位置上再叠加一个 steps·step —— 否则会先冲过头再被 renderBoard 拉回。
    const items = group
      .map((p) => this.tileAt(p.x, p.y))
      .filter((t): t is TileSprite => t !== null)
      .map((t) => {
        const home = this.cellCenter(t.gx, t.gy);
        return {
          t,
          fromX: t.x,
          fromY: t.y,
          toX: home.x + movedX,
          toY: home.y + movedY,
        };
      });
    return tween({
      duration: 180,
      ease: easeOutCubic,
      onUpdate: (v) => {
        for (const it of items) {
          it.t.x = it.fromX + (it.toX - it.fromX) * v;
          it.t.y = it.fromY + (it.toY - it.fromY) * v;
        }
      },
    });
  }

  // 把某格的方块从棋盘网格「摘出」到特效层，用于播放不阻塞输入的消除动画。
  // 摘出后 renderBoard 清空 tilesLayer 时不会销毁它（它已在 fxLayer）。
  takeTile(x: number, y: number): TileSprite | null {
    const t = this.tiles[y]?.[x] ?? null;
    if (!t) return null;
    this.tiles[y][x] = null;
    this.fxLayer.addChild(t); // 重新挂父级（自动从 tilesLayer 移除）
    return t;
  }

  // 非阻塞消除特效：光束 → 粒子 → ghost 淡出并销毁。
  // 逻辑棋盘应在调用前就已提交，本方法纯视觉，不参与游戏状态。
  async playEliminate(
    a: Pos,
    b: Pos,
    color: number,
    ghosts: (TileSprite | null)[],
  ): Promise<void> {
    const ca = this.cellCenter(a.x, a.y);
    const cb = this.cellCenter(b.x, b.y);
    await beam(this.fxLayer, ca, cb, this.pack.beamColor ?? 0x38bdf8);
    burst(this.fxLayer, ca, color);
    burst(this.fxLayer, cb, color);
    await Promise.all(ghosts.map((g) => this.fadeOut(g)));
    for (const g of ghosts) g?.destroy({ children: true });
  }

  private fadeOut(t: TileSprite | null): Promise<void> {
    if (!t) return Promise.resolve();
    return tween({
      duration: 380,
      ease: easeOutCubic,
      onUpdate: (v) => {
        t.scale.set(1 - v);
        t.rotation = v * Math.PI;
        t.alpha = 1 - v;
      },
    });
  }

  /** 无效操作时的棋盘抖动反馈。 */
  shake(): Promise<void> {
    const base = this.boardX;
    return tween({
      duration: 360,
      onUpdate: (v) => {
        this.board.x = base + Math.sin(v * Math.PI * 5) * (1 - v) * 8;
      },
      onComplete: () => (this.board.x = base),
    });
  }

  // 洗牌动画：位置不变、只重新分配 Kind。用「整齐翻牌」表现洗牌过程 ——
  // 每张牌先水平翻面（scaleX→0），翻到侧棱时换成新种类，再翻回正面（scaleX→1）。
  // 按对角线错峰（gx+gy）形成一道扫过棋盘的波浪，比瞬间替换直观得多。
  async playShuffle(next: Board): Promise<void> {
    // 动画期间冻结布局，避免 resize 触发 renderBoard 把正在翻转的方块销毁掉。
    this.suspendLayout = true;
    const cardColor = this.pack.tileCardColor ?? 0xf8fafc;
    const flips: { t: TileSprite; kind: Kind }[] = [];
    for (let y = 0; y < this.rows; y++) {
      for (let x = 0; x < this.cols; x++) {
        const t = this.tiles[y]?.[x];
        const kind = next[y]?.[x];
        if (t && kind != null) flips.push({ t, kind });
      }
    }

    const HALF = 150; // 单侧翻转时长
    const WAVE = 45; // 每级对角线的错峰间隔
    await Promise.all(
      flips.map(({ t, kind }) =>
        delay((t.gx + t.gy) * WAVE)
          .then(() =>
            tween({
              duration: HALF,
              ease: easeInOutQuad,
              onUpdate: (v) => (t.scale.x = 1 - v),
            }),
          )
          .then(() => {
            // 侧棱瞬间换牌面，再从 0 翻回，视觉上就是「翻过来是新牌」。
            t.paint(kind, tileVisual(this.pack, kind), this.cell, cardColor);
            t.scale.x = 0;
            return tween({
              duration: HALF,
              ease: easeInOutQuad,
              onUpdate: (v) => (t.scale.x = v),
            });
          }),
      ),
    );
    // 收尾：解冻布局并全量重建，确保可视对象与数据严格对齐（也修正残留缩放）。
    this.suspendLayout = false;
    this.renderBoard(next);
  }

  /** 提示高亮：给指定格子套一圈脉冲光环。 */
  highlight(cells: Pos[]): void {
    for (const c of cells) {
      const t = this.tileAt(c.x, c.y);
      if (!t) continue;
      const ring = new Graphics();
      const s = this.cell;
      ring
        .roundRect(-s / 2 - 4, -s / 2 - 4, s + 8, s + 8, s * 0.26)
        .stroke({ width: 4, color: 0xfacc15, alpha: 1 });
      ring.alpha = 0;
      t.addChildAt(ring, 0);
      let cycles = 0;
      const pulse = (): Promise<void> =>
        tween({
          duration: 420,
          ease: easeInOutQuad,
          onUpdate: (v) => (ring.alpha = Math.sin(v * Math.PI)),
        }).then(() => {
          if (++cycles < 3 && ring.parent) return pulse();
          ring.destroy();
          return undefined;
        });
      void pulse();
    }
  }
}
